import { Accordion } from "@app/shared/Accordion"
import { InfoPopover } from "@app/shared/InfoPopover";
import { FC, JSX, useContext } from "react"
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import { ProviderNetworkTypeEnum, providerNetworkTypeDisplayOrder } from "@app/shared/enums/providerNetworkTypeEnum";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

export const ProviderProfileNetworkIntegrity: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const { selectedProvider, isNiMarketProviderProcessing } = providerHubContext;

  const hasNonMarketTypes = selectedProvider.providerNetworkTypes?.some(
    t => t === ProviderNetworkTypeEnum.Network || t === ProviderNetworkTypeEnum.Prospect
  ) ?? false;
  const showStillProcessing = isNiMarketProviderProcessing && !hasNonMarketTypes;

  // Market-only providers have no trustworthy excess results until the Market excess calculation ships
  const showMarketExcessWarnings = useContext(FeatureFlagContext).enabledFeatureFlags.has(Features.EXCESS_WARNING_FIELDS_FOR_MARKET_PROVIDERS);
  const showExcessRows = showMarketExcessWarnings || hasNonMarketTypes;

  const interpretBooleanValues = (value?: boolean | null): JSX.Element => {
    if (value === true) return <><span className="provider-profile__network-integrity-value__network-integrity-failure">Yes</span><NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true} /></>;
    if (value === false) return <span>No</span>;
    return <span>Package Not Licensed</span>;
  }

  const renderExcessSourceBadges = (testIdPrefix: string, sources: Partial<Record<ProviderNetworkTypeEnum, boolean>>): JSX.Element | null => {
    const triggeringSources = providerNetworkTypeDisplayOrder.filter(type => sources[type]);
    if (triggeringSources.length === 0) return null;
    return (
      <>
        {triggeringSources.map(type => (
          <ProviderNetworkTypeBadge
            badgeStyle={ProviderBadgeStyle.Outline}
            key={type}
            providerNetworkType={type}
            testId={`${testIdPrefix}-${type}`}
          />
        ))}
      </>
    );
  };

  const parseLineOfBusiness = (lob: string): string => {
    const [, lineOfBusiness, lineOfBusinessType] = lob?.split("|") ?? [null, "Line of Business", null];

    switch (lineOfBusiness) {
      case "Medicare":
      case "Medicaid":
        return lineOfBusiness;
      case "Marketplace":
      case "Employer Sponsored":
        return "Commercial";
      case "Military":
        return lineOfBusiness + " - " + lineOfBusinessType;
      default:
        return lineOfBusiness;
    }
  }

  return (
    <Accordion title="Network Integrity" testId="provider-profile__network-integrity-accordion" defaultOpen={true} className="provider-profile__network-integrity-accordion">
      <dl>
        {showStillProcessing ? (
          <div className="provider-profile__network-integrity-row">
            <dt className="provider-profile__network-integrity-label">Still Processing</dt>
            <dd className="provider-profile__network-integrity-value"></dd>
          </div>
        ) : (
          <>
            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Deactivated NPI</dt>
              <dd className={`provider-profile__network-integrity-value--deactivated-npi`}>
                <span>{interpretBooleanValues(selectedProvider.isNpiDeactivated)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Invalid NPI</dt>
              <dd className={`provider-profile__network-integrity-value--invalid-npi`}>
                <span>{interpretBooleanValues(selectedProvider.isNpiInvalid)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Missing NPI</dt>
              <dd className={`provider-profile__network-integrity-value--missing-npi`}>
                <span>{interpretBooleanValues(selectedProvider.isNpiMissing)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">OIG Exclusion</dt>
              <dd className={`provider-profile__network-integrity-value--oig-excluded`}>
                <span>{interpretBooleanValues(selectedProvider.hasOigExclusion)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Medicare Opt-Out</dt>
              <dd className={`provider-profile__network-integrity-value--medicare-opt-out`}>
                <span>{interpretBooleanValues(selectedProvider.hasMedicareOptOut)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Medicare Preclusion</dt>
              <dd className={`provider-profile__network-integrity-value--medicare-precluded`}>
                <span>{interpretBooleanValues(selectedProvider.hasMedicarePreclusion)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Expired License</dt>
              <dd className={`provider-profile__network-integrity-value--expired-license`}>
                <span>{interpretBooleanValues(selectedProvider.isPractitionerLicenseExpired)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Ghost Provider ({parseLineOfBusiness(selectedProvider.ghostLineOfBusiness)} Claims)</dt>
              <dd className={`provider-profile__network-integrity-value--ghostlob`}>
                <span>{interpretBooleanValues(selectedProvider.isProviderGhostForLob)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Ghost Provider (Total Claims)</dt>
              <dd className={`provider-profile__network-integrity-value--ghost`}>
                <span>{interpretBooleanValues(selectedProvider.isProviderGhost)}</span>
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">Retired</dt>
              <dd className={`provider-profile__network-integrity-value--retired`}>
                <span>{interpretBooleanValues(selectedProvider.isPractitionerRetired)}</span>
              </dd>
            </div>

            {showExcessRows && (<>
            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">
                Excess Addresses
                <InfoPopover
                  content="The Excess Addresses warning is calculated using only Network, only Prospect, or only Market access points. It does not combine access points from different provider sources when counting towards the threshold of excessiveness."
                  triggerTestId="provider-profile__network-integrity-excess-addresses-info"
                />
              </dt>
              <dd className={`provider-profile__network-integrity-value--excess-addresses`}>
                {selectedProvider.hasExcessLocations === true ? (
                  <>
                    <span className="provider-profile__network-integrity-value__network-integrity-failure">Yes</span>
                    <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true} />
                    {renderExcessSourceBadges("provider-profile-excess-addresses-source-badge", {
                      [ProviderNetworkTypeEnum.Network]: selectedProvider.hasExcessLocationsNetwork,
                      [ProviderNetworkTypeEnum.Prospect]: selectedProvider.hasExcessLocationsProspect,
                      [ProviderNetworkTypeEnum.Market]: selectedProvider.hasExcessLocationsMarket,
                    })}
                  </>
                ) : (
                  <span>{interpretBooleanValues(selectedProvider.hasExcessLocations)}</span>
                )}
              </dd>
            </div>

            <div className="provider-profile__network-integrity-row">
              <dt className="provider-profile__network-integrity-label">
                Excess Specialties
                <InfoPopover
                  content="The Excess Specialties warning is calculated using only Network, only Prospect, or only Market access points. It does not combine access points from different provider sources when counting towards the threshold of excessiveness."
                  triggerTestId="provider-profile__network-integrity-excess-specialties-info"
                />
              </dt>
              <dd className={`provider-profile__network-integrity-value--excess-specialties`}>
                {selectedProvider.hasExcessSpecialties === true ? (
                  <>
                    <span className="provider-profile__network-integrity-value__network-integrity-failure">Yes</span>
                    <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true} />
                    {renderExcessSourceBadges("provider-profile-excess-specialties-source-badge", {
                      [ProviderNetworkTypeEnum.Network]: selectedProvider.hasExcessSpecialtiesNetwork,
                      [ProviderNetworkTypeEnum.Prospect]: selectedProvider.hasExcessSpecialtiesProspect,
                      [ProviderNetworkTypeEnum.Market]: selectedProvider.hasExcessSpecialtiesMarket,
                    })}
                  </>
                ) : (
                  <span>{interpretBooleanValues(selectedProvider.hasExcessSpecialties)}</span>
                )}
              </dd>
            </div>
            </>)}
          </>
        )}
      </dl>
    </Accordion>
  )
};